import { useCallback } from 'react';

type DateFormatType = 'short' | 'full' | 'dash' | 'dot' | 'custom';

const DDMMYYYY_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;

export const useFormatDate = () => {
    const parseDate = useCallback((dateString?: string): Date | null => {
        if (!dateString) return null;

        const match = DDMMYYYY_REGEX.exec(dateString);

        if (match) {
            const [, day, month, year] = match;
            const parsedDay = Number(day);
            const parsedMonth = Number(month);
            const parsedYear = Number(year);

            const date = new Date(parsedYear, parsedMonth - 1, parsedDay);

            if (
                date.getFullYear() === parsedYear &&
                date.getMonth() === parsedMonth - 1 &&
                date.getDate() === parsedDay
            ) {
                return date;
            }

            return null;
        }

        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return null;

        return date;
    }, []);

    const formatDate = useCallback(
        (dateString?: string, format: DateFormatType = 'full', customFormat?: string): string => {
            const date = parseDate(dateString);
            if (!date) return '';

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            if (format === 'custom' && customFormat) {
                return customFormat.replace('DD', day).replace('MM', month).replace('YYYY', String(year));
            }

            switch (format) {
                case 'short':
                    return `${day}/${month}`;
                case 'dash':
                    return `${day}-${month}-${year}`;
                case 'dot':
                    return `${day}.${month}.${year}`;
                case 'full':
                default:
                    return `${day}/${month}/${year}`;
            }
        },
        [parseDate]
    );

    return { formatDate };
};
